import React from 'react';
import styled from 'styled-components';
import { } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1537726235470-8504e3beef77?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const SubTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #e0e0e0;
  text-align: center;
  font-style: italic;
  font-weight: normal;
`;

 

 

 

 

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 4rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: #004744;
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
    color: #004744;
  }
  
  p {
    color: #e0e0e0;
    line-height: 1.6;
  }
`;

const NumberedListContainer = styled.div`
  margin: 3rem 0;
`;

const NumberedItem = styled.div`
  display: flex;
  margin-bottom: 2.5rem;
`;

const NumberCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #004744;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  margin-right: 1.5rem;
  flex-shrink: 0;
`;

const NumberedContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: #004744;
  }
  
  p {
    color: #e0e0e0;
    line-height: 1.6;
  }
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const HomeStaging: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const stepsData = [
    {
      number: 2,
      title: language === 'it' ? 'VALUTAZIONE SPAZI E DESIGN' : 'SPACE AND DESIGN ASSESSMENT',
      description: language === 'it' 
        ? 'Analizziamo spazi, arredi e palette cromatiche in funzione del target clienti e del mercato locale, definendo un concept di design mirato a massimizzare appeal e conversioni.'
        : 'We analyze spaces, furnishings and color palettes based on target customers and local market, defining a design concept aimed at maximizing appeal and conversions.'
    },
    {
      number: 3,
      title: language === 'it' ? 'ARREDAMENTO E STAGING' : 'FURNISHING AND STAGING',
      description: language === 'it' 
        ? 'Arrediamo e prepariamo la proprietà per presentarla nel miglior modo possibile ai potenziali ospiti. Selezioniamo mobili, decorazioni e accessori che migliorano l\'attrattiva visiva della proprietà.'
        : 'We furnish and prepare the property to present it in the best possible way to potential guests. We select furniture, decorations, and accessories that enhance the visual appeal of the property.'
    },
    {
      number: 4,
      title: language === 'it' ? 'FOTOGRAFIA PROFESSIONALE' : 'PROFESSIONAL PHOTOGRAPHY',
      description: language === 'it' 
        ? 'Realizziamo servizi fotografici professionali per catturare la bellezza e le caratteristiche uniche della proprietà. Le immagini di alta qualità sono fondamentali per attirare l\'attenzione online.'
        : 'We conduct professional photo shoots to capture the beauty and unique features of the property. High-quality images are essential for attracting attention online.'
    },
    {
      number: 5,
      title: language === 'it' ? 'PROMOZIONE ONLINE' : 'ONLINE PROMOTION',
      description: language === 'it' 
        ? 'Una volta che la proprietà è stata trasformata attraverso il Home Staging, viene promossa attraverso piattaforme OTA. Le immagini professionali e la presentazione curata aumentano significativamente le possibilità di attrarre ospiti e generare prenotazioni.'
        : 'Once the property has been transformed through Home Staging, it is promoted through OTA platforms. Professional images and curated presentation significantly increase the chances of attracting guests and generating bookings.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Home Staging | J-O-H' : 'Home Staging | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio professionale di Home Staging per valorizzare la tua proprietà e massimizzare il potenziale di guadagno con gli affitti brevi' 
            : 'Professional Home Staging service to enhance your property and maximize earning potential with short-term rentals'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>HOME STAGING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Il Home Staging è un servizio professionale che valorizza le caratteristiche della tua proprietà, preparandola per il mercato degli affitti brevi. Attraverso un attento studio degli spazi, l\'arredamento strategico e l\'illuminazione ottimale, trasformiamo la tua proprietà in uno spazio accogliente e desiderabile.'
              : 'Home Staging is a professional service that enhances the features of your property, preparing it for the short-term rental market. Through careful study of spaces, strategic furnishing, and optimal lighting, we transform your property into a welcoming and desirable space.'}
          </Description>
          
          <NumberedListContainer>
            {stepsData.map((step) => (
              <NumberedItem key={step.number}>
                <NumberCircle>{step.number}</NumberCircle>
                <NumberedContent>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </NumberedContent>
              </NumberedItem>
            ))}
          </NumberedListContainer>
          
          
          
          <Description>
            {language === 'it' 
              ? 'Il Home Staging non è solo una questione estetica, ma una strategia di marketing immobiliare. Una proprietà ben presentata ottiene più visualizzazioni online, attira ospiti di qualità e può essere affittata a tariffe più elevate, massimizzando il tuo ritorno sull\'investimento.'
              : 'Home Staging is not just about aesthetics, but a real estate marketing strategy. A well-presented property gets more views online, attracts quality guests, and can be rented at higher rates, maximizing your return on investment.'}
          </Description>
          
          <FeatureList>
            <FeatureItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <FeatureText>
                <h3>{language === 'it' ? 'Maggiore Visibilità Online' : 'Greater Online Visibility'}</h3>
                <p>
                  {language === 'it'
                    ? 'Le proprietà con home staging professionale ricevono fino al 40% in più di visualizzazioni sulle piattaforme di prenotazione.'
                    : 'Properties with professional home staging receive up to 40% more views on booking platforms.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <FeatureText>
                <h3>{language === 'it' ? 'Tempi di Prenotazione Ridotti' : 'Reduced Booking Times'}</h3>
                <p>
                  {language === 'it'
                    ? 'Le proprietà stagionate vengono prenotate più rapidamente, riducendo i periodi di inattività e aumentando il tuo reddito annuale.'
                    : 'Staged properties are booked more quickly, reducing downtime and increasing your annual income.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <FeatureText>
                <h3>{language === 'it' ? 'Tariffe Più Elevate' : 'Higher Rates'}</h3>
                <p>
                  {language === 'it'
                    ? 'Una proprietà ben presentata può giustificare tariffe più elevate, aumentando il tuo margine di profitto.'
                    : 'A well-presented property can justify higher rates, increasing your profit margin.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <FeatureText>
                <h3>{language === 'it' ? 'Ospiti di Qualità' : 'Quality Guests'}</h3>
                <p>
                  {language === 'it'
                    ? 'Una presentazione professionale attira ospiti che apprezzano e rispettano gli ambienti di qualità.'
                    : 'A professional presentation attracts guests who appreciate and respect quality environments.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi una Consulenza' : 'Request a Consultation'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default HomeStaging; 